import { Helmet } from 'react-helmet'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { HashLink as Link } from 'react-router-hash-link'
import { motion, useAnimationControls } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MainHeader from '../../../components/MainHeader'
import MainFooter from '../../../components/MainFooter'
import CTAButton from '../../../components/CTAButton'
import AnimatedQuote from '../../../components/AnimatedQuote'
import PricingCard from '../../../components/PricingCard'
import { boxvar1, boxvar3, boxvar2_1, boxvar2_2, boxvar2dyndelay, oPU, oP } from '../../../core.js'
import { faPhone, faChevronDown, faMagnifyingGlassArrowRight, faTags, faImages } from '@fortawesome/free-solid-svg-icons'
import packageJson from '../../../../package.json'

import testimonial_joely from '../../../assets/pictures/testimonials/testimonial_joely.webp'
import cam_photography from '../../../assets/pictures/sectionimages/cam_photography-cut_compressed.webp'

import '../../Pricing/Pricing.css'
import '../../Pricing/MPricing.css'
import '../../Home/Home.css'
import '../../Home/MHome.css'
import './FunnelFoto.css'
import './MFunnelFoto.css'
import useProgressiveHeroBg from '../../../hooks/useProgressiveHeroBg'

const PORTFOLIO_URL = 'https://www.picdrop.com/tonetwork/ton-portfolio-gesamt-2026'

const scrolldowniconanim = {
    idle: {
        y: [0, 6, 0],
        scale: 1,
        transition: { duration: 1.5, repeat: Infinity, ease: 'easeOut' },
    },
    hover: {
        y: [0, 3, 0],
        scale: 1.2,
        transition: { duration: 1.0, repeat: Infinity, ease: 'easeInOut' },
    },
}

const CALENDLY = 'https://calendly.com/tonetworkde-meet/getintouch'
const STAND = packageJson.pricingdate
const SITE = 'https://tonetwork.de'

const REGIONS = {
    memmingen: {
        city: 'Memmingen',
        region: 'Unterallgäu',
        canonical: '/landing/photograph-memmingen',
        seoTitle: 'Fotograf Memmingen – Eventfotos, Business & Video | ToNetwork',
        seoDescription: 'Fotograf in Memmingen: Eventfotos ab 590 €, Business-Shootings ab 990 €, Social-Video-Content ab 290 €/Monat. ToNetwork – über 10 Jahre Erfahrung, Express-Lieferung in 48 Std.',
        h1: 'Fotografie & Video in Memmingen – Bilder, die wirken',
        h1sub: 'Eventfotos, Business-Shootings und Social-Video-Content – professionell und regional.',
        localLead: 'David Heidl und das ToNetwork-Team begleiten Events, Firmen und Marken in Memmingen und im Unterallgäu – mit über 10 Jahren Erfahrung unter dem Namen davidfilmt.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Hawangen', 'Babenhausen', 'Türkheim'],
    },
    unterallgaeu: {
        city: 'Unterallgäu',
        region: 'Allgäu',
        canonical: '/landing/photograph-unterallgaeu',
        seoTitle: 'Fotograf Unterallgäu – Event- & Businessfotografie ab 190 € | ToNetwork',
        seoDescription: 'Fotograf im Unterallgäu: Porträts, Eventfotos und Business-Shootings. Video-Content für Social Media. Partner aus Hawangen – Nutzungsrechte inklusive.',
        h1: 'Fotografie & Video im Unterallgäu – deine Marke im besten Licht',
        h1sub: 'Vom Porträt bis zum Aftermovie – Fotos und Bewegtbild aus einer Hand.',
        localLead: 'Wir kennen die Region, die Locations und die Stimmung eurer Events – und liefern Bilder, die Lust auf mehr machen.',
        areaServed: ['Unterallgäu', 'Memmingen', 'Türkheim', 'Mindelheim', 'Bad Wörishofen'],
    },
    allgaeu: {
        city: 'Allgäu',
        region: 'Schwaben',
        canonical: '/landing/photograph-allgaeu',
        seoTitle: 'Fotograf Allgäu – Eventfotos, Business & Videografie | ToNetwork',
        seoDescription: 'Fotograf im Allgäu: Event-Paket ab 590 €, Business-Tag ab 990 €, Social Content ab 290 €/Monat. ToNetwork – professionelle Fotografie und Video aus dem Unterallgäu.',
        h1: 'Fotografie & Video im Allgäu – Bilder, die Geschichten erzählen',
        h1sub: 'Eventfotos, Team-Shootings und Reels – für Unternehmen, Veranstalter und Marken.',
        localLead: 'Ob Memmingen, Kempten oder das ländliche Allgäu: Wir sind vor Ort, mittendrin – und liefern in 48 Stunden.',
        areaServed: ['Allgäu', 'Memmingen', 'Kempten', 'Unterallgäu', 'Oberallgäu'],
    },
    kempten: {
        city: 'Kempten',
        region: 'Allgäu',
        canonical: '/landing/photograph-kempten',
        seoTitle: 'Fotograf Kempten – Event- & Businessfotografie | ToNetwork',
        seoDescription: 'Fotograf in Kempten und Umgebung: Eventfotos, Business-Shootings und Social-Video-Content. ToNetwork – persönlicher Partner aus dem Allgäu.',
        h1: 'Fotografie & Video in Kempten & Allgäu – professionell, express, nah',
        h1sub: 'Starke Bilder und zeitgemäßer Video-Content – damit dein Auftritt hängen bleibt.',
        localLead: 'Für Firmen und Veranstalter in Kempten und der Region: kurze Wege, direkte Kommunikation und Bilder, die online und offline funktionieren.',
        areaServed: ['Kempten', 'Allgäu', 'Memmingen', 'Sonthofen', 'Immenstadt'],
    },
    default: {
        city: 'Memmingen',
        region: 'Allgäu',
        canonical: '/funnel/foto',
        seoTitle: 'Fotografie & Videografie – Eventfotos, Business & Social Video | ToNetwork',
        seoDescription: 'Foto & Video von ToNetwork: Shooting Basic ab 190 €, Event-Paket ab 590 €, Business-Tag ab 990 €, Social Content ab 290 €/Monat. Memmingen, Unterallgäu, Allgäu.',
        h1: 'Deine Marke verdient bessere Bilder',
        h1sub: 'Eventfotos, Business-Shootings und Social-Video-Content – aus einer Hand.',
        localLead: 'Aus Hawangen im Unterallgäu produzieren wir Fotografie und Video für Events, Unternehmen und Marken – mit Nutzungsrechten inklusive und Express-Lieferung.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Allgäu', 'Kempten'],
    },
}

function resolveRegion(pathname) {
    if (pathname.includes('memmingen')) return REGIONS.memmingen
    if (pathname.includes('unterallgaeu')) return REGIONS.unterallgaeu
    if (pathname.includes('allgaeu')) return REGIONS.allgaeu
    if (pathname.includes('kempten')) return REGIONS.kempten
    return REGIONS.default
}

const fotoBasic = {
    name: 'Shooting Basic',
    price: 'ab 190 €',
    priceNote: 'einmalig',
    forwho: 'Porträts & Team',
    sales: 'Echte Menschen, echte Wirkung. Professionelle Porträts, die zu dir und deiner Marke passen.',
    features: [
        { t: 'bis 2 Std. Dauer', in: null },
        { t: 'bis 15 bearbeitete Bilder', in: null },
        { t: 'Express-Lieferung optional', in: false },
        { t: 'Nutzungsrechte inklusive', in: true },
    ],
    paket: 'foto-basic',
}

const fotoEvent = {
    name: 'Event-Paket',
    badge: 'Allrounder',
    featured: true,
    price: 'ab 590 €',
    priceNote: 'einmalig',
    forwho: 'Veranstaltungen & Partys',
    sales: 'Eure Veranstaltung verdient Bilder, die die Stimmung einfangen. Wir sind mittendrin – ihr bekommt Fotos, die Lust auf das nächste Event machen.',
    features: [
        { t: 'bis 5 Std. Dauer', in: null },
        { t: 'bis 100 bearbeitete Bilder', in: null },
        { t: 'Express-Lieferung in 48 Std.', in: true },
        { t: 'Nutzungsrechte inklusive', in: true },
    ],
    paket: 'foto-event',
}

const fotoBusiness = {
    name: 'Business-Tag',
    price: 'ab 990 €',
    priceNote: 'einmalig',
    forwho: 'Firmen & Produkte',
    sales: 'Ein Tag, alle Motive: Team, Räume, Produkte. Die komplette Bildwelt für euren Auftritt – online wie gedruckt.',
    features: [
        { t: 'ganzer Tag (8 Std.)', in: null },
        { t: 'bis 200 bearbeitete Bilder', in: null },
        { t: 'Express-Lieferung in 48 Std.', in: true },
        { t: 'Nutzungsrechte inklusive', in: true },
        { t: 'Vorgespräche zur Szenen-Gestaltung', in: true },
    ],
    paket: 'foto-business',
}

const contentStarter = {
    name: 'Starter DIY',
    price: '290 €',
    priceNote: 'pro Monat',
    forwho: 'Erste Sichtbarkeit',
    sales: 'Regelmäßiger Content auf deinem wichtigsten Kanal – wir geben die Anleitung und du produzierst den Content selbst.',
    features: [
        { t: '4 Posts / Monat', in: null },
        { t: '1 Reel / Video', in: null },
        { t: 'Plattformen nach Wahl', in: null },
        { t: 'Redaktionsplan', in: true },
        { t: 'Video-Produktion inklusive', in: false },
        { t: 'Community-Management', in: false },
        { t: 'Monatsreport', in: false },
    ],
    paket: 'content-starter',
}

const contentWachstum = {
    name: 'Wachstum',
    badge: '👍🏼 Top Preis-Leistungs',
    featured: true,
    price: '590 €',
    priceNote: 'pro Monat',
    forwho: 'Mehr Reichweite',
    sales: 'Mehr Frequenz, mehr Bewegtbild, mehr Interaktion – der Hebel für spürbares Wachstum. Mit unserer Anleitung produzierst du den Content ganz einfach selbst.',
    features: [
        { t: '8 Posts / Monat', in: null },
        { t: '4 Reels / Videos', in: null },
        { t: 'Plattformen: Instagram, Facebook, TikTok', in: null },
        { t: 'Redaktionsplan', in: true },
        { t: 'Video-Produktion inklusive', in: false },
        { t: 'Basic Community-Management (einfache Reaktionen)', in: true },
        { t: 'Monatsreport', in: true },
    ],
    paket: 'content-wachstum',
}

const painPoints = [
    {
        title: 'Handy-Fotos wirken unprofessionell',
        text: 'Der erste Eindruck zählt – auf der Website, in Social Media und bei Google. Schlechte Bilder kosten Vertrauen, bevor du überhaupt angefragt wirst.',
    },
    {
        title: 'Events ohne starke Fotos verpuffen',
        text: 'Die Stimmung war grandios – aber ohne professionelle Bilder bleibt sie unsichtbar. Kein Material für Social Media, Sponsoren oder die Bewerbung des nächsten Events.',
    },
    {
        title: 'Video und Reels fehlen komplett',
        text: 'Statische Bilder reichen nicht mehr. Wer auf TikTok, Instagram und LinkedIn sichtbar sein will, braucht Bewegtbild – und jemanden, der das kann.',
    },
]

const faqsForRegion = (city) => [
    {
        q: `Was kostet ein Fotograf in ${city}?`,
        a: 'Unser Einstieg Shooting Basic startet ab 190 €, das Event-Paket ab 590 € und der Business-Tag ab 990 €. Für laufenden Video- und Social-Content gibt es monatliche Pakete ab 290 €. Alle Preise sind Listenpreise ohne Rabatte – nach einem kostenlosen Erstgespräch erhältst du ein individuelles Festangebot.',
    },
    {
        q: 'Wie schnell bekomme ich die Bilder?',
        a: 'Im Event-Paket und Business-Tag ist Express-Lieferung in 48 Stunden inklusive. Beim Shooting Basic ist Express optional.',
    },
    {
        q: 'Darf ich die Bilder überall nutzen?',
        a: 'Ja. In allen Foto-Paketen sind Nutzungsrechte inklusive – für Website, Social Media, Print und Werbematerial. Details regelt das individuelle Angebot gemäß unserer AGB.',
    },
    {
        q: 'Macht ihr auch Video und Aftermovies?',
        a: 'Ja. Neben Eventfotos bieten wir Social-Video-Content und Aftermovies an – als Einzelprojekt oder über unsere monatlichen Content-Pakete. Auch Influencer-Support (Schnitt, Kamera, Licht) ist möglich.',
    },
]

function localBusinessJsonLd(region) {
    return {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'ToNetwork UG (haftungsbeschränkt)',
        description: region.seoDescription,
        url: SITE,
        telephone: '+49-8332-4908872',
        email: 'hey@tonetwork.de',
        address: {
            '@type': 'PostalAddress',
            streetAddress: 'Hawangen',
            addressLocality: 'Hawangen',
            postalCode: '87749',
            addressRegion: 'Bayern',
            addressCountry: 'DE',
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: 47.998,
            longitude: 10.283,
        },
        areaServed: region.areaServed.map((name) => ({ '@type': 'City', name })),
        priceRange: '€€',
        knowsAbout: ['Fotografie', 'Eventfotografie', 'Videografie', 'Social Media Content', 'Businessportraits'],
    }
}

export default function FunnelFoto() {
    const { pathname } = useLocation()
    const region = resolveRegion(pathname)
    const canonicalUrl = SITE + region.canonical
    const faqs = faqsForRegion(region.city)

    const controls = useAnimationControls()

    useEffect(() => {
        controls.start('idle')
    }, [controls])

    useProgressiveHeroBg('sec_start--funnel-foto')

    return (
        <div className="mainCon funnel_foto" id="mainCon--funnel-foto">

            <MainHeader funnel disableanimation={false} />

            <Helmet>
                <meta charSet="utf-8" />
                <title>{region.seoTitle}</title>
                <link rel="canonical" href={canonicalUrl} />
                <meta name="description" content={region.seoDescription} />
                <meta name="keywords" content={`Fotograf ${region.city}, Eventfotograf ${region.city}, Businessfotografie ${region.region}, Videograf ${region.city}, ToNetwork`} />
                <meta property="og:title" content={region.seoTitle} />
                <meta property="og:description" content={region.seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="de_DE" />
                <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd(region))}</script>
            </Helmet>

            <section className="sec_start progressive-hero-bg" id="sec_start--funnel-foto">

                <motion.div
                    variants={boxvar1}
                    initial="hidden"
                    exit="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: '-50px' }}
                    className="start_heading_cont"
                >
                    <AnimatedQuote className="start_quote" text={region.h1} />

                    <p className="start_about">
                        {region.h1sub}<br />
                        <br />
                        {region.localLead}<br />
                        <br />
                        Pakete ab <b className="start_about_highlight">190 €</b> – Eventfotos, Business-Shootings & Video-Content
                    </p>
                </motion.div>

                <motion.div variants={boxvar3} initial="hidden" exit="hidden" whileInView="visible" viewport={{ once: false, margin: '-50px' }}>
                    <div className="sec_start_cta_row">
                        <CTAButton className="sec_start_btn" showArrow={false} customIcon={faPhone} iconLeft={true} title="Kostenloses Erstgespräch" onclick={() => { oPU(CALENDLY) }} />
                        <CTAButton className="sec_start_btn sec_start_btn-chat" showArrow={false} lightcta={true} customIcon={faImages} iconLeft={true} title="Portfolio ansehen" onclick={() => { oP(PORTFOLIO_URL) }} />
                    </div>
                </motion.div>

                <Link smooth to="#sec_next--funnel-foto">
                    <motion.div
                        className="sec_next_icon_div noselect"
                        onHoverStart={() => controls.start('hover')}
                        onHoverEnd={() => controls.start('idle')}
                    >
                        <span>Erfahre mehr</span>
                        <motion.div variants={scrolldowniconanim} animate={controls}>
                            <FontAwesomeIcon icon={faChevronDown} className="sec_next_icon_arrow" />
                        </motion.div>
                    </motion.div>
                </Link>

            </section>

            <section className="sec_next" id="sec_next--funnel-foto">
                <div className="sec_next_cont" id="sec_next--funnel-foto--cont">

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_foto_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Kommt dir das bekannt vor?</h2>
                        <h3>Drei Probleme, die wir in {region.city} und Umgebung täglich sehen.</h3>
                        <div className="funnel_foto_pain_grid">
                            {painPoints.map((p, i) => (
                                <motion.div
                                    key={p.title}
                                    variants={boxvar2dyndelay}
                                    custom={0.15 * (i + 1)}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: false, margin: '-50px' }}
                                    className="funnel_foto_pain_card"
                                >
                                    <span className="funnel_foto_pain_num">{String(i + 1).padStart(2, '0')}</span>
                                    <h4>{p.title}</h4>
                                    <p>{p.text}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_foto_section pricing_section"
                        id="funnel-foto-shootings"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Fotografie-Pakete</h2>
                        <h3>Porträt, Event oder Business-Tag – du wählst den Umfang.</h3>
                        <p className="pricing_lead">
                            Bilder sagen mehr als tausend Worte – vor allem, wenn sie professionell inszeniert sind.
                            Alle Pakete inklusive Nutzungsrechte. Event und Business mit Express-Lieferung in 48 Stunden.
                        </p>
                        <div className="pricing_grid">
                            <PricingCard tier={fotoBasic} delay={0.15} />
                            <PricingCard tier={fotoEvent} delay={0.3} />
                            <PricingCard tier={fotoBusiness} delay={0.45} />
                        </div>
                        <p className="pricing_note">
                            Alle Preise netto, zzgl. MwSt. – Listenpreise ohne Rabatte. Jedes Paket wird individuell auf dein Projekt zugeschnitten. Stand: {STAND}
                        </p>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_foto_section pricing_section"
                        id="funnel-foto-video"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Video & Social Content</h2>
                        <h3>Reels, Posts und Bewegtbild – regelmäßig und planbar.</h3>
                        <p className="pricing_lead">
                            Neben Fotografie bieten wir Social-Media-Content und Videoproduktion: Aftermovies, Recruiting-Kampagnen,
                            Produktmarketing und Influencer-Support. Monatliche Pakete mit Redaktionsplan und Reels.
                        </p>
                        <div className="pricing_grid">
                            <PricingCard tier={contentStarter} delay={0.15} />
                            <PricingCard tier={contentWachstum} delay={0.3} />
                        </div>
                        <p className="pricing_note">
                            Alle Preise netto, zzgl. MwSt. – Listenpreise ohne Rabatte. Stand: {STAND}
                        </p>
                        <p className="funnel_foto_crosslink">
                            Event geplant? Schau auch unser{' '}
                            <a href="/funnel/veranstalter">Event-Boost Komplettpaket</a>
                            {' '}(Landingpage + Fotos + Social Content ab 1.490 €).
                        </p>
                    </motion.div>

                    <motion.div
                        variants={boxvar2dyndelay}
                        className="small_services_box funnel_foto_portfolio_box"
                        initial="hidden"
                        whileInView="visible"
                        custom={0.2}
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="small_services_textbox">
                            <h3>Über 10 Jahre Erfahrung – sieh selbst.</h3>
                            <br /><br />
                            <p>
                                David Heidl ist seit vielen Jahren unter dem Namen <strong>davidfilmt</strong> bekannt –
                                Party, Messe, Event oder Ehrenamt. In unserem Portfolio findest du Eventfotos,
                                Business-Shootings und Video-Content aus der Region.
                            </p>
                            <CTAButton
                                showArrow={false}
                                customIcon={faImages}
                                iconLeft={true}
                                title="Portfolio entdecken"
                                onclick={() => { oP(PORTFOLIO_URL) }}
                            />
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_2}
                        className="funnel_foto_section funnel_foto_why"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="funnel_foto_why_text">
                            <h2>Warum ToNetwork?</h2>
                            <h3>Fotografie, Video und Digitalisierung – aus einer Hand.</h3>
                            <p>
                                Wir sind nicht nur Fotografen, sondern dein Partner für den gesamten digitalen Auftritt.
                                Von Eventfotos über Aftermovies bis zur Website – alles aus einer Hand, mit festem Ansprechpartner.
                            </p>
                            <ul className="funnel_foto_benefits">
                                <li>Express-Lieferung in 48 Std. (Event & Business)</li>
                                <li>Nutzungsrechte inklusive</li>
                                <li>Eventfotos, Aftermovies & Social Content</li>
                                <li>Über 10 Jahre Erfahrung – davidfilmt</li>
                                <li>Erstberatung kostenlos, Festangebot ohne versteckte Kosten</li>
                            </ul>
                            <CTAButton
                                showArrow={false}
                                customIcon={faMagnifyingGlassArrowRight}
                                iconLeft={true}
                                title="Alle Media-Leistungen"
                                href="/services/content"
                            />
                        </div>
                        <img
                            src={cam_photography}
                            alt={`Fotograf und Videograf in ${region.city}`}
                            className="funnel_foto_why_img"
                            loading="lazy"
                        />
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_foto_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Das sagen unsere Kunden</h2>
                        <div className="funnel_foto_testimonial">
                            <span className="funnel_foto_testimonial_quote">
                                „Dank der Fotos von David erstrahlen unsere Events in neuem Licht."
                            </span>
                            <div className="funnel_foto_testimonial_person">
                                <img src={testimonial_joely} alt="Joely White, Veranstalter Queerparadise Party" />
                                <div>
                                    <span className="funnel_foto_testimonial_name">Joely White</span>
                                    <span className="funnel_foto_testimonial_company">Veranstalter, Queerparadise Party</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_foto_section pricing_section"
                        id="funnel-foto-faq"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Häufige Fragen</h2>
                        <div className="pricing_faq_list">
                            {faqs.map((f, i) => (
                                <details key={i} className="pricing_faq_item" open={i === 0}>
                                    <summary>{f.q}</summary>
                                    <p>{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        variants={boxvar2dyndelay}
                        className="small_services_box small_services_box-bigbottom funnel_foto_cta_box"
                        initial="hidden"
                        whileInView="visible"
                        custom={0.3}
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="small_services_textbox">
                            <h3>Bereit für dein Shooting in {region.city}?</h3>
                            <br />
                            <p>
                                In einem kostenlosen 30-Minuten-Gespräch klären wir, welches Paket zu deinem Event,
                                Unternehmen oder Projekt passt – ehrlich und ohne Verkaufsdruck. Alle Preise hier sind
                                Listenpreise ohne Rabatte und werden individuell für dich ausgestaltet.
                            </p>
                            <CTAButton
                                showArrow={false}
                                customIcon={faPhone}
                                iconLeft={true}
                                title="Kostenloses Erstgespräch"
                                onclick={() => { oPU(CALENDLY) }}
                            />
                            <br />
                            <p className="pricing_toni_hint">
                                Erst Orientierung gefragt? <a href="/chat">ToNi beantwortet Fragen zu Paketen und Preisen</a> – unverbindlich im Chat.
                            </p>
                        </div>
                    </motion.div>

                    <br />
                    <CTAButton
                        showArrow={false}
                        customIcon={faTags}
                        iconLeft={true}
                        title="Alle weiteren Pakete & Preise"
                        href="/pricing"
                    />

                    <p className="pricing_footnote">
                        Es gelten unsere <a href="/rechtliches/agb">Allgemeinen Geschäftsbedingungen (AGB)</a>
                    </p>

                    <br /><br /><br />
                </div>
            </section>

            <MainFooter />
        </div>
    )
}
